/**
 * IFRAP-AI.fr - Module Préparation Chronique Média AI
 * 
 * Fonctionnalités:
 * - Scan veille automatique 7h (sauf samedi)
 * - Suggestions de sujets basées sur l'actualité
 * - Suggestions d'angles (livre Agnès, travaux IFRAP, ADN média)
 * - Input voix ou texte pour les idées
 * - Génération de chronique dans le style d'Agnès
 * - Lancement présentateur + relances
 * - Infographies pour CNews
 * - Export Word/PDF/Email
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { 
  Radio, Tv, Mic, MicOff, Send, RefreshCw, FileText, 
  Download, Mail, Edit3, Check, ChevronRight, Clock,
  Sparkles, AlertTriangle, Copy, Image, Presentation, ExternalLink, Globe
} from 'lucide-react';
import PublishContentDialog from '../../components/PublishContentDialog';

// Configuration des médias avec logos
const MEDIA_CONFIG = {
  europe1: {
    name: 'Europe 1',
    icon: Radio,
    color: '#0066CC',
    logo: '/logos/europe1.png',
    presentateur: 'Dimitri Pavlenko',
    emission: 'La Matinale',
    style: 'Direct, accessible, chiffres clairs. Ton pédagogique.',
    dureeStandard: '2min30',
    adn: 'Radio généraliste populaire, audience large, ton accessible mais rigoureux.',
  },
  cnews: {
    name: 'CNews',
    icon: Tv,
    color: '#E30613',
    logo: '/logos/cnews.png',
    presentateur: 'Romain Desarbres',
    emission: 'La Matinale',
    style: 'Punchy, engagé, positions claires. Infographies à l\'écran.',
    dureeStandard: '3min',
    adn: 'Chaîne info conservatrice, débat affirmé, positions économiques libérales.',
  },
  francetv: {
    name: 'France Télévisions',
    icon: Tv,
    color: '#0055A4',
    logo: '/logos/francetv.png',
    presentateur: '',
    emission: 'Matinale',
    style: 'Équilibré, pédagogique, service public.',
    dureeStandard: '3min',
    adn: 'Service public, pluralisme, accessibilité.',
  },
  figarotv: {
    name: 'Le Figaro TV',
    icon: Tv,
    color: '#00529F',
    logo: '/logos/figarotv.png',
    presentateur: '',
    emission: 'Débat économique',
    style: 'Analytique, économique, libéral.',
    dureeStandard: '5min',
    adn: 'Ligne éditoriale économique libérale, audience CSP+.',
  },
  bfmtv: {
    name: 'BFM TV',
    icon: Tv,
    color: '#FF6600',
    logo: '/logos/bfmtv.jpg',
    presentateur: '',
    emission: 'Good Morning Business',
    style: 'Rythmé, info continue, breaking news.',
    dureeStandard: '2min',
    adn: 'Chaîne info en continu, format court, réactivité.',
  },
  generique: {
    name: 'Autre média',
    icon: Tv,
    color: '#4A235A',
    logo: '/logos/ifrap.png',
    presentateur: '',
    emission: '',
    style: 'À personnaliser selon le média.',
    dureeStandard: '3min',
    adn: 'À définir selon le contexte.',
  },
};

// Types d'intervention
const INTERVENTION_TYPES = [
  { id: 'chronique', label: 'Chronique économique', duree: '2-3 min' },
  { id: 'debat', label: 'Participation débat', duree: '10-15 min' },
  { id: 'interview', label: 'Interview plateau', duree: '5-7 min' },
  { id: 'reaction', label: 'Réaction à chaud', duree: '1-2 min' },
];

const ChroniquePage = () => {
  // États principaux
  const [selectedMedia, setSelectedMedia] = useState('europe1');
  const [interventionType, setInterventionType] = useState('chronique');
  const [customConfig, setCustomConfig] = useState({
    presentateur: '',
    emission: '',
    duree: '3min',
    notes: '',
  });

  // États de génération
  const [step, setStep] = useState(1); // 1: Config, 2: Sujets, 3: Brief, 4: Chronique, 5: Export
  const [loading, setLoading] = useState(false);
  const [veilleData, setVeilleData] = useState(null);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [suggestedAngles, setSuggestedAngles] = useState([]);
  const [selectedAngle, setSelectedAngle] = useState(null);
  
  // Brief utilisateur
  const [userBrief, setUserBrief] = useState('');
  const [userAttaque, setUserAttaque] = useState('');
  const [userChute, setUserChute] = useState('');
  const [userPoints, setUserPoints] = useState('');
  
  // Speech-to-Text (Web Speech API natif)
  const [isRecording, setIsRecording] = useState(false);
  const [sttSupported, setSttSupported] = useState(false);
  const recognitionRef = useRef(null);
  const sttTargetRef = useRef('userBrief'); // which field receives STT output

  // Chronique générée
  const [chronique, setChronique] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedChronique, setEditedChronique] = useState('');
  
  // Infographies
  const [infographies, setInfographies] = useState([]);

  // Export
  const [exportEmail, setExportEmail] = useState('');
  const [exportStatus, setExportStatus] = useState(null);
  const [gammaExporting, setGammaExporting] = useState(false);
  const [gammaResult, setGammaResult] = useState(null);
  
  // Publication
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  useEffect(() => {
    // Vérifier le support Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSttSupported(true);
      const recognition = new SpeechRecognition();
      recognition.lang = 'fr-FR';
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }
        if (finalTranscript) {
          const target = sttTargetRef.current;
          if (target === 'userBrief') setUserBrief(prev => prev + finalTranscript);
          else if (target === 'userAttaque') setUserAttaque(prev => prev + finalTranscript);
          else if (target === 'userChute') setUserChute(prev => prev + finalTranscript);
          else if (target === 'userPoints') setUserPoints(prev => prev + finalTranscript);
        }
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, []);

  // Charger la veille du jour
  const loadVeille = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/veille/feed', {
        params: { limit: 50 }
      });
      setVeilleData(res.data);
      
      // Générer des suggestions de sujets
      await generateTopicSuggestions(res.data.articles || []);
    } catch (err) {
      console.error('Erreur chargement veille:', err);
    } finally {
      setLoading(false);
    }
  };

  // Générer des suggestions de sujets
  const generateTopicSuggestions = async (articles) => {
    setLoading(true);
    try {
      const res = await api.post('/api/chronique/suggest-topics', {
        articles: articles.slice(0, 20),
        media: selectedMedia,
      });
      setSuggestedTopics(res.data.topics || []);
    } catch (err) {
      // En cas d'erreur, générer des suggestions basiques
      const topics = articles.slice(0, 5).map(a => ({
        title: a.title,
        source: a.source_name,
        summary: a.summary || a.description,
        relevance: 'haute',
        angle_ifrap: 'À définir selon analyse IFRAP',
      }));
      setSuggestedTopics(topics);
    } finally {
      setLoading(false);
    }
  };

  // Générer des angles
  const generateAngles = async (topic) => {
    setLoading(true);
    try {
      const res = await api.post('/api/chronique/suggest-angles', {
        topic,
        media: selectedMedia,
        intervention_type: interventionType,
      });
      setSuggestedAngles(res.data.angles || []);
    } catch (err) {
      // Angles par défaut basés sur l'ADN IFRAP
      setSuggestedAngles([
        { id: 1, title: 'Angle finances publiques', description: 'Impact sur la dette et la dépense publique' },
        { id: 2, title: 'Angle compétitivité', description: 'Conséquences sur les entreprises françaises' },
        { id: 3, title: 'Angle comparaison internationale', description: 'Comment font nos voisins européens ?' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Start/Stop Speech-to-Text
  const startRecording = (target = 'userBrief') => {
    if (recognitionRef.current) {
      sttTargetRef.current = target;
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  // Générer la chronique
  const generateChronique = async () => {
    setLoading(true);
    try {
      const mediaConfig = MEDIA_CONFIG[selectedMedia];
      const res = await api.post('/api/chronique/generate', {
        topic: selectedTopic,
        angle: selectedAngle,
        media: selectedMedia,
        media_config: selectedMedia === 'generique' ? customConfig : mediaConfig,
        intervention_type: interventionType,
        user_brief: userBrief,
        user_attaque: userAttaque,
        user_chute: userChute,
        user_points: userPoints,
      });
      
      setChronique(res.data.chronique);
      setEditedChronique(res.data.chronique?.texte || '');
      setInfographies(res.data.infographies || []);
      
      // Auto-save to history
      saveToHistory(res.data.chronique);
      
      setStep(4);
    } catch (err) {
      console.error('Erreur génération:', err);
      // Générer un template par défaut
      const template = generateDefaultTemplate();
      setChronique(template);
      setEditedChronique(template.texte);
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  // Template par défaut si l'API n'est pas disponible
  const generateDefaultTemplate = () => {
    const media = MEDIA_CONFIG[selectedMedia];
    const presentateur = selectedMedia === 'generique' 
      ? customConfig.presentateur 
      : media.presentateur;

    return {
      lancement: `[LANCEMENT ${presentateur?.toUpperCase() || 'PRÉSENTATEUR'}]\n"${selectedTopic?.title || 'Sujet du jour'}... Agnès Verdier-Molinié, quelle est votre analyse ?"`,
      texte: `[À COMPLÉTER - Chronique sur: ${selectedTopic?.title || 'Sujet'}]\n\n${userBrief || 'Développement à rédiger...'}\n\n[ATTAQUE SUGGÉRÉE]\n${userAttaque || 'À définir...'}\n\n[CHUTE SUGGÉRÉE]\n${userChute || 'À définir...'}`,
      relances: [
        `"Et concrètement, qu'est-ce que ça change pour les Français ?"`,
        `"Comment se situe la France par rapport à ses voisins ?"`,
        `"Que préconise l'IFRAP ?"`,
      ],
      duree_estimee: selectedMedia === 'generique' ? customConfig.duree : media.dureeStandard,
    };
  };

  // Export Word (via backend IFRAP template)
  const exportWord = async () => {
    try {
      const res = await api.post('/api/export/chronique/word', {
        chronique: {
          ...chronique,
          texte: editedChronique || chronique?.texte || '',
        },
        media_name: MEDIA_CONFIG[selectedMedia].name,
        topic: selectedTopic?.title || '',
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Chronique_IFRAP_${MEDIA_CONFIG[selectedMedia].name.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export Word error:', err);
      alert('Erreur lors de l\'export Word');
    }
  };

  // Export PDF (via backend IFRAP template)
  const exportPDF = async () => {
    try {
      const res = await api.post('/api/export/chronique/pdf', {
        chronique: {
          ...chronique,
          texte: editedChronique || chronique?.texte || '',
        },
        media_name: MEDIA_CONFIG[selectedMedia].name,
        topic: selectedTopic?.title || '',
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Chronique_IFRAP_${MEDIA_CONFIG[selectedMedia].name.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export PDF error:', err);
      alert('Erreur lors de l\'export PDF');
    }
  };

  // Envoyer par email
  const sendByEmail = async () => {
    if (!exportEmail) {
      alert('Veuillez entrer une adresse email');
      return;
    }
    
    setExportStatus('sending');
    try {
      await api.post('/api/chronique/send-email', {
        email: exportEmail,
        chronique: chronique,
        infographies: infographies,
        media: selectedMedia,
      });
      setExportStatus('success');
    } catch (err) {
      setExportStatus('error');
    }
  };

  // Save chronique to history
  const saveToHistory = async (chroniqueData, gammaUrl = null) => {
    try {
      await api.post('/api/history/chronique', {
        media: MEDIA_CONFIG[selectedMedia].name,
        topic: selectedTopic?.title || 'Sans titre',
        chronique: {
          ...chroniqueData,
          infographies: infographies,
        },
        gamma_url: gammaUrl,
      });
    } catch (err) {
      console.error('Error saving to history:', err);
    }
  };

  // Export Gamma
  const exportGamma = async () => {
    setGammaExporting(true);
    setGammaResult(null);
    
    try {
      const res = await api.post('/api/export/gamma', {
        title: `Chronique ${MEDIA_CONFIG[selectedMedia].name} - ${selectedTopic?.title || 'Sujet'}`,
        content: editedChronique || chronique?.texte || '',
        num_cards: 10,
        export_as: null, // Don't export as PDF, just get the URL
        content_type: 'chronique',
        chronique: {
          ...chronique,
          texte: editedChronique || chronique?.texte || '',
        },
        media_name: MEDIA_CONFIG[selectedMedia].name,
        topic: selectedTopic?.title || '',
      });
      
      if (res.data.success) {
        const generationId = res.data.generation_id;
        
        // If we have a URL immediately, show it
        if (res.data.gamma_url) {
          setGammaResult({
            success: true,
            url: res.data.gamma_url,
            message: 'Présentation créée !',
          });
        } else if (generationId) {
          // Poll for status
          setGammaResult({
            success: true,
            pending: true,
            generationId,
            message: 'Génération en cours...',
          });
          
          // Start polling
          pollGammaStatus(generationId);
        }
      } else if (res.data.config_required) {
        setGammaResult({
          success: false,
          config_required: true,
          message: res.data.error,
          help_url: res.data.help_url,
        });
      } else {
        setGammaResult({
          success: false,
          message: res.data.error || 'Erreur lors de la création',
        });
      }
    } catch (err) {
      console.error('Gamma export error:', err);
      setGammaResult({
        success: false,
        message: err.response?.data?.error || 'Erreur de connexion',
      });
    } finally {
      setGammaExporting(false);
    }
  };

  // Poll Gamma status
  const pollGammaStatus = async (generationId) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 * 5s = 2.5 minutes max
    
    const checkStatus = async () => {
      try {
        const res = await api.get(`/api/export/gamma/${generationId}`);
        
        if (res.data.gamma_url) {
          setGammaResult({
            success: true,
            url: res.data.gamma_url,
            message: 'Présentation créée !',
          });
          return;
        }
        
        if (res.data.status === 'failed') {
          setGammaResult({
            success: false,
            message: 'Échec de la génération',
          });
          return;
        }
        
        // Still pending
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          setGammaResult({
            success: false,
            message: 'Délai dépassé. Vérifiez votre compte Gamma.',
          });
        }
      } catch (err) {
        console.error('Gamma status check error:', err);
      }
    };
    
    setTimeout(checkStatus, 5000);
  };

  // Formater pour export avec template professionnel IFRAP
  const formatForExport = () => {
    const media = MEDIA_CONFIG[selectedMedia];
    const date = new Date().toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return `
      <div style="text-align: center; margin-bottom: 30px;">
        <img src="/logos/ifrap.png" alt="IFRAP" style="height: 60px; margin-bottom: 10px;" onerror="this.style.display='none'" />
        <h1 style="color: #000091; margin: 0;">Fondation IFRAP</h1>
        <p style="color: #666; font-size: 12px; margin: 5px 0;">Institut Français pour la Recherche sur les Administrations et les Politiques Publiques</p>
      </div>

      <div style="border: 2px solid #000091; padding: 20px; margin-bottom: 30px;">
        <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 15px;">
          <img src="${media.logo}" alt="${media.name}" style="height: 40px;" onerror="this.style.display='none'" />
          <div>
            <h2 style="margin: 0; color: #000091;">Chronique ${media.name}</h2>
            <p style="margin: 0; color: #666; font-size: 14px;">${date}</p>
          </div>
        </div>
        <table style="width: 100%; font-size: 14px;">
          <tr><td style="color: #666; width: 150px;">Chroniqueuse:</td><td><strong>Agnès Verdier-Molinié</strong></td></tr>
          <tr><td style="color: #666;">Sujet:</td><td><strong>${selectedTopic?.title || 'Non défini'}</strong></td></tr>
          <tr><td style="color: #666;">Émission:</td><td>${media.emission || 'À définir'}</td></tr>
          <tr><td style="color: #666;">Durée:</td><td>${chronique?.duree_estimee || media.dureeStandard}</td></tr>
        </table>
      </div>
      
      <h2 style="color: #000091; border-bottom: 2px solid #E1000F; padding-bottom: 10px;">LANCEMENT</h2>
      <div style="background: #f5f5f5; padding: 15px; border-left: 4px solid #000091; margin-bottom: 20px;">
        <p style="font-style: italic; margin: 0; font-size: 14px; color: #333;">
          <em>« ${chronique?.lancement || ''} »</em>
        </p>
      </div>
      
      <h2 style="color: #000091; border-bottom: 2px solid #E1000F; padding-bottom: 10px;">CHRONIQUE</h2>
      <div style="padding: 15px 0; line-height: 1.8; font-size: 15px;">
        ${(editedChronique || chronique?.texte || '').split('\n').map(p => `<p style="margin-bottom: 12px;">${p}</p>`).join('')}
      </div>
      
      <h2 style="color: #000091; border-bottom: 2px solid #E1000F; padding-bottom: 10px;">RELANCES SUGGÉRÉES</h2>
      ${(chronique?.relances || []).map((r, i) => `
        <div style="background: #fff3cd; padding: 12px 15px; margin: 8px 0; border-radius: 4px;">
          <strong style="color: #856404;">${i + 1}.</strong> <em>${r}</em>
        </div>
      `).join('')}
      
      ${infographies.length > 0 ? `
        <h2 style="color: #E1000F; border-bottom: 2px solid #E1000F; padding-bottom: 10px;">INFOGRAPHIES</h2>
        ${infographies.map((info, i) => `
          <div style="border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 4px;">
            <strong style="color: #000091;">Infographie ${i + 1}:</strong> ${info.titre}<br/>
            <p style="margin: 10px 0;">${info.contenu}</p>
            <small style="color: #666;">Source: ${info.source}</small>
          </div>
        `).join('')}
      ` : ''}

      <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #666;">
        <p><strong>Document préparé par IFRAP-AI</strong></p>
        <p style="margin-top: 10px;">
          © ${new Date().getFullYear()} IFRAP - Fondation pour la Recherche sur les Administrations et les Politiques Publiques<br/>
          5, rue Cadet - 75009 Paris | www.ifrap.org
        </p>
        <p style="margin-top: 10px; font-style: italic;">
          Ce document peut être librement utilisé et diffusé sous réserve de citer la source : 
          « IFRAP - www.ifrap.org »
        </p>
      </div>
    `;
  };

  // Sélection du sujet
  const handleSelectTopic = (topic) => {
    setSelectedTopic(topic);
    generateAngles(topic);
    setStep(3);
  };

  // Rendu conditionnel selon l'étape
  const renderStep = () => {
    switch (step) {
      case 1:
        return renderConfigStep();
      case 2:
        return renderTopicsStep();
      case 3:
        return renderBriefStep();
      case 4:
        return renderChroniqueStep();
      case 5:
        return renderExportStep();
      default:
        return renderConfigStep();
    }
  };

  // ÉTAPE 1: Configuration
  const renderConfigStep = () => (
    <div className="space-y-6">
      {/* Sélection du média */}
      <div>
        <h3 className="text-sm font-medium text-gray-400 mb-3">Sélectionnez le média</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(MEDIA_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            const isSelected = selectedMedia === key;
            return (
              <button
                key={key}
                onClick={() => setSelectedMedia(key)}
                data-testid={`media-${key}`}
                className={`p-4 rounded-xl border-2 transition-all text-left bg-white ${
                  isSelected 
                    ? 'border-[#000091] shadow-lg' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  {config.logo ? (
                    <img 
                      src={config.logo} 
                      alt={config.name}
                      className="h-8 w-auto object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Icon className="w-6 h-6" style={{ color: config.color }} />
                  )}
                  <span className="font-medium text-gray-900">{config.name}</span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2">{config.adn}</p>
                {isSelected && (
                  <div className="mt-2 text-xs text-[#000091] font-medium flex items-center gap-1">
                    <Check className="w-3 h-3" /> Sélectionné
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Configuration personnalisée pour générique */}
      {selectedMedia === 'generique' && (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-[#00FF88]">Personnalisation</h4>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={customConfig.presentateur}
              onChange={(e) => setCustomConfig({ ...customConfig, presentateur: e.target.value })}
              placeholder="Nom du présentateur"
              className="px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            />
            <input
              type="text"
              value={customConfig.emission}
              onChange={(e) => setCustomConfig({ ...customConfig, emission: e.target.value })}
              placeholder="Nom de l'émission"
              className="px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            />
            <input
              type="text"
              value={customConfig.duree}
              onChange={(e) => setCustomConfig({ ...customConfig, duree: e.target.value })}
              placeholder="Durée (ex: 3min)"
              className="px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            />
            <textarea
              value={customConfig.notes}
              onChange={(e) => setCustomConfig({ ...customConfig, notes: e.target.value })}
              placeholder="Notes sur le média/émission..."
              className="px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
              rows={2}
            />
          </div>
        </div>
      )}

      {/* Type d'intervention */}
      <div>
        <h3 className="text-sm font-medium text-gray-400 mb-3">Type d'intervention</h3>
        <div className="flex flex-wrap gap-2">
          {INTERVENTION_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setInterventionType(type.id)}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                interventionType === type.id
                  ? 'bg-[#00FF88] text-black font-medium'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {type.label} ({type.duree})
            </button>
          ))}
        </div>
      </div>

      {/* Bouton suivant */}
      <button
        onClick={() => { loadVeille(); setStep(2); }}
        className="w-full py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 transition-colors flex items-center justify-center gap-2"
      >
        Scanner la veille du jour
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  // ÉTAPE 2: Sujets suggérés
  const renderTopicsStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Sujets suggérés</h3>
        <button
          onClick={loadVeille}
          disabled={loading}
          className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {suggestedTopics.map((topic, idx) => (
            <button
              key={idx}
              onClick={() => handleSelectTopic(topic)}
              className="w-full text-left p-4 bg-[#1a1a2e] border border-gray-700 rounded-lg hover:border-[#00FF88]/50 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-white mb-1">{topic.title}</h4>
                  <p className="text-sm text-gray-400 mb-2">{topic.summary?.slice(0, 150)}...</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">{topic.source}</span>
                    <span className={`px-2 py-0.5 rounded ${
                      topic.relevance === 'haute' 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      Pertinence {topic.relevance}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            </button>
          ))}
        </div>
      )}

      <button
        onClick={() => setStep(1)}
        className="text-sm text-gray-400 hover:text-white"
      >
        ← Retour à la configuration
      </button>
    </div>
  );

  // ÉTAPE 3: Brief utilisateur
  const renderBriefStep = () => (
    <div className="space-y-6">
      {/* Sujet sélectionné */}
      <div className="bg-[#000091]/10 border border-[#000091]/30 rounded-lg p-4">
        <h4 className="text-sm text-[#00FF88] mb-1">Sujet sélectionné</h4>
        <p className="text-white font-medium">{selectedTopic?.title}</p>
      </div>

      {/* Angles suggérés */}
      <div>
        <h3 className="text-sm font-medium text-gray-400 mb-3">Angles suggérés (ADN IFRAP)</h3>
        <div className="space-y-2">
          {suggestedAngles.map((angle, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedAngle(angle)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selectedAngle?.id === angle.id
                  ? 'border-[#00FF88] bg-[#00FF88]/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <p className="font-medium text-white text-sm">{angle.title}</p>
              <p className="text-xs text-gray-400">{angle.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Input utilisateur */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-gray-400">Votre brief (écrit ou dicté)</label>
            {sttSupported && (
              <button
                onClick={isRecording ? stopRecording : () => startRecording('userBrief')}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                  isRecording 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                data-testid="stt-toggle-brief"
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                {isRecording ? 'Arrêter la dictée' : 'Dicter'}
              </button>
            )}
          </div>
          <textarea
            value={userBrief}
            onChange={(e) => setUserBrief(e.target.value)}
            placeholder="Exprimez votre vision du sujet, le traitement que vous voulez appliquer, vos points d'attention..."
            className="w-full h-32 px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white text-sm resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400 block mb-2">Idée d'attaque (optionnel)</label>
            <textarea
              value={userAttaque}
              onChange={(e) => setUserAttaque(e.target.value)}
              placeholder="Votre idée pour démarrer..."
              className="w-full h-20 px-3 py-2 bg-black/30 border border-gray-700 rounded-lg text-white text-sm resize-none"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 block mb-2">Idée de chute (optionnel)</label>
            <textarea
              value={userChute}
              onChange={(e) => setUserChute(e.target.value)}
              placeholder="Votre idée pour conclure..."
              className="w-full h-20 px-3 py-2 bg-black/30 border border-gray-700 rounded-lg text-white text-sm resize-none"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-400 block mb-2">Points d'attention particuliers</label>
          <textarea
            value={userPoints}
            onChange={(e) => setUserPoints(e.target.value)}
            placeholder="Ce que vous voulez absolument mentionner, éviter, etc."
            className="w-full h-20 px-3 py-2 bg-black/30 border border-gray-700 rounded-lg text-white text-sm resize-none"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => setStep(2)}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          ← Retour
        </button>
        <button
          onClick={generateChronique}
          disabled={loading}
          className="flex-1 py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Générer la chronique
            </>
          )}
        </button>
      </div>
    </div>
  );

  // ÉTAPE 4: Chronique générée
  const renderChroniqueStep = () => {
    const media = MEDIA_CONFIG[selectedMedia];
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-white">Chronique {media.name}</h3>
            <p className="text-sm text-gray-400">Durée estimée: {chronique?.duree_estimee}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isEditing ? 'bg-[#00FF88] text-black' : 'bg-gray-700 text-white'
              }`}
            >
              <Edit3 className="w-4 h-4" />
              {isEditing ? 'Terminer' : 'Modifier'}
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(editedChronique)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600"
            >
              <Copy className="w-4 h-4" />
              Copier
            </button>
          </div>
        </div>

        {/* Lancement présentateur */}
        <div className="bg-[#000091]/10 border border-[#000091]/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-[#00FF88] mb-2 flex items-center gap-2">
            <Radio className="w-4 h-4" />
            Lancement {chronique?.presentateur || media.presentateur}
          </h4>
          <pre className="text-white text-sm whitespace-pre-wrap font-mono">
            {chronique?.lancement}
          </pre>
        </div>

        {/* Texte de la chronique */}
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-400 mb-3">Texte chronique</h4>
          {isEditing ? (
            <textarea
              value={editedChronique}
              onChange={(e) => setEditedChronique(e.target.value)}
              className="w-full h-64 bg-black/30 border border-gray-600 rounded-lg p-3 text-white text-sm font-mono resize-none"
            />
          ) : (
            <pre className="text-white text-sm whitespace-pre-wrap">
              {editedChronique}
            </pre>
          )}
        </div>

        {/* Relances */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-400 mb-3">Relances suggérées</h4>
          <div className="space-y-2">
            {(chronique?.relances || []).map((relance, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-yellow-400 text-sm">{idx + 1}.</span>
                <p className="text-white text-sm italic">{relance}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Infographies (CNews) */}
        {(selectedMedia === 'cnews' || infographies.length > 0) && (
          <div className="bg-[#E30613]/10 border border-[#E30613]/30 rounded-lg p-4">
            <h4 className="text-sm font-medium text-[#E30613] mb-3 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Infographies pour l'écran ({infographies.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {infographies.map((info, idx) => (
                <div key={idx} className="bg-black/30 rounded-lg p-3">
                  <p className="text-white font-medium text-sm mb-1">{info.titre}</p>
                  <p className="text-gray-300 text-sm">{info.contenu}</p>
                  <p className="text-xs text-gray-500 mt-2">Source: {info.source}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => setStep(3)}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            ← Retour
          </button>
          <button
            onClick={() => setStep(5)}
            className="flex-1 py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 transition-colors flex items-center justify-center gap-2"
          >
            Exporter
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  // ÉTAPE 5: Export
  const renderExportStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-white">Exporter la chronique</h3>

      {/* Options d'export */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={exportWord}
          className="p-4 bg-[#1a1a2e] border border-gray-700 rounded-lg hover:border-blue-500/50 transition-all text-left"
          data-testid="export-word-btn"
        >
          <FileText className="w-8 h-8 text-blue-400 mb-2" />
          <p className="font-medium text-white">Export Word</p>
          <p className="text-xs text-gray-500">Document .docx éditable</p>
        </button>

        <button
          onClick={exportPDF}
          className="p-4 bg-[#1a1a2e] border border-gray-700 rounded-lg hover:border-red-500/50 transition-all text-left"
          data-testid="export-pdf-btn"
        >
          <Download className="w-8 h-8 text-red-400 mb-2" />
          <p className="font-medium text-white">Export PDF</p>
          <p className="text-xs text-gray-500">Document imprimable</p>
        </button>

        {/* Export Gamma */}
        <div className="p-4 bg-[#1a1a2e] border border-gray-700 rounded-lg hover:border-purple-500/50 transition-all">
          <Presentation className="w-8 h-8 text-purple-400 mb-2" />
          <p className="font-medium text-white mb-2">Export Gamma</p>
          <p className="text-xs text-gray-500 mb-3">Présentation automatique</p>
          
          {gammaResult?.success && gammaResult.url ? (
            <a
              href={gammaResult.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 w-full py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 justify-center"
            >
              <ExternalLink className="w-4 h-4" />
              Ouvrir dans Gamma
            </a>
          ) : gammaResult?.pending ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-purple-400">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Génération en cours...</span>
              </div>
              <p className="text-xs text-gray-500">
                La présentation sera prête dans quelques secondes
              </p>
            </div>
          ) : gammaResult?.config_required ? (
            <div className="space-y-2">
              <p className="text-xs text-yellow-400">{gammaResult.message}</p>
              <a
                href={gammaResult.help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-400 hover:underline"
              >
                Obtenir une clé API Gamma
              </a>
            </div>
          ) : (
            <button
              onClick={exportGamma}
              disabled={gammaExporting}
              className="w-full py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="export-gamma-btn"
            >
              {gammaExporting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer présentation'
              )}
            </button>
          )}
          
          {gammaResult && !gammaResult.success && !gammaResult.config_required && !gammaResult.pending && (
            <p className="text-xs text-red-400 mt-2">{gammaResult.message}</p>
          )}
        </div>

        {/* Email */}
        <div className="p-4 bg-[#1a1a2e] border border-gray-700 rounded-lg">
          <Mail className="w-8 h-8 text-green-400 mb-2" />
          <p className="font-medium text-white mb-2">Envoyer par email</p>
          <input
            type="email"
            value={exportEmail}
            onChange={(e) => setExportEmail(e.target.value)}
            placeholder="votre@email.com"
            className="w-full px-3 py-2 bg-black/30 border border-gray-600 rounded text-white text-sm mb-2"
            data-testid="export-email-input"
          />
          <button
            onClick={sendByEmail}
            disabled={!exportEmail || exportStatus === 'sending'}
            className="w-full py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            data-testid="export-email-btn"
          >
            {exportStatus === 'sending' ? 'Envoi...' : 
             exportStatus === 'success' ? '✓ Envoyé' :
             exportStatus === 'error' ? 'Erreur - Réessayer' : 
             'Envoyer'}
          </button>
        </div>
      </div>

      {/* Récapitulatif */}
      <div className="bg-gray-800/50 rounded-lg p-4 text-sm">
        <h4 className="text-gray-400 mb-2">Récapitulatif</h4>
        <ul className="text-gray-300 space-y-1">
          <li>• Média: {MEDIA_CONFIG[selectedMedia].name}</li>
          <li>• Sujet: {selectedTopic?.title}</li>
          <li>• Durée: {chronique?.duree_estimee}</li>
          <li>• Infographies: {infographies.length}</li>
        </ul>
      </div>

      {/* Publier sur le front */}
      <div className="bg-gradient-to-r from-[#000091]/20 to-purple-900/20 border border-[#000091]/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-[#000091]" />
            <div>
              <p className="font-medium text-white">Publier sur IFRAP-AI</p>
              <p className="text-xs text-gray-400">Rendre cette chronique visible sur le front public</p>
            </div>
          </div>
          <button
            onClick={() => setShowPublishDialog(true)}
            className="px-4 py-2 bg-[#000091] text-white font-medium rounded-lg hover:bg-[#000091]/90 transition-colors flex items-center gap-2"
            data-testid="publish-chronique-btn"
          >
            <Globe className="w-4 h-4" />
            Publier
          </button>
        </div>
      </div>

      {/* Dialogue de publication */}
      <PublishContentDialog
        isOpen={showPublishDialog}
        onClose={(success) => {
          setShowPublishDialog(false);
          if (success) {
            // Optionally show success toast
          }
        }}
        contentType="chronique"
        title={`${selectedTopic?.title || 'Chronique'} - ${MEDIA_CONFIG[selectedMedia].name}`}
        content={editedChronique || chronique?.texte || ''}
        sourceId={chronique?.id}
        gammaUrl={gammaResult?.url}
      />

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => setStep(4)}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          ← Retour
        </button>
        <button
          onClick={() => {
            setStep(1);
            setSelectedTopic(null);
            setSelectedAngle(null);
            setChronique(null);
            setUserBrief('');
            setUserAttaque('');
            setUserChute('');
            setGammaResult(null);
          }}
          className="flex-1 py-3 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 transition-colors"
        >
          Nouvelle chronique
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Préparation Chronique Média</h1>
          <p className="text-gray-400 text-sm mt-1">Assistant IA pour vos interventions</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          Scan veille: 7h00
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s 
                  ? 'bg-[#00FF88] text-black' 
                  : 'bg-gray-700 text-gray-400'
              }`}
            >
              {s}
            </div>
            {s < 5 && (
              <div className={`flex-1 h-1 rounded ${step > s ? 'bg-[#00FF88]' : 'bg-gray-700'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Contenu principal */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
        {renderStep()}
      </div>
    </div>
  );
};

export default ChroniquePage;
